import React, { useEffect, useState } from 'react';

// reference uploader instance
import { Uploader } from '../../wrapped-cube-client/uploader';
import { Upload } from '@cube3/common/model/schema/resources/file-upload';
import { useSelector } from 'react-redux';
import { selectors } from '../../redux/ducks/resource-nodes';
import { useCallback } from 'react';
import { useDeleteResource__ALPHA } from '../../redux/components/Hooks/useDeleteResource';

// helper hooks + derived HOC
export interface WithCanResumeProps {
  canResume: boolean;
}

export const useCanResume = (id: string) => {
  const [canResume, setCanResume] = useState(false);
  useEffect(() => {
    let canceled = false;
    Uploader.canResume(id).then((can) => {
      if (!canceled) {
        setCanResume(can);
      }
    });
    return () => {
      canceled = true;
    };
  }, [id]);
  return canResume;
};

/** Returns a function to pause or resume an upload  */
export const useTogglePauseUpload = (uploadId) => {
  const upload: Upload = useSelector((state) => {
    return selectors.getResourceById(state, 'file-upload', uploadId);
  });

  const togglePauseUpload = useCallback(() => {
    if (!upload) {
      return;
    }
    if (Uploader.isUserPaused(upload) || Uploader.isQueued(upload)) {
      Uploader.unpauseUpload(upload);
    } else {
      Uploader.pauseUpload(upload);
    }
  }, [upload]);

  return { togglePauseUpload, upload };
};

/** Returns if the upload is in paused state. */
export const isUserPaused = (upload: Upload) => {
  return Uploader.isUserPaused(upload);
};

export const withCanResume = (config) => {
  return (Wrapped) => (props) => {
    const uuid =
      typeof config.uuid === 'function' ? config.uuid(props) : config.uuid;

    const can = useCanResume(uuid);
    return <Wrapped {...props} canResume={can} />;
  };
};

export interface WithFailedUploadActionsProps {
  onRetryUpload(): void;
  onRemoveUpload(): void;
}

export const withFailedUploadActions = (config) => {
  return (Wrapped) => (props) => {
    const upload =
      typeof config.upload === 'function'
        ? config.upload(props)
        : config.upload;
    const assetId =
      typeof config.assetId === 'function'
        ? config.assetId(props)
        : config.assetId;

    const [deleteResource] = useDeleteResource__ALPHA({
      cacheInvalidator: useCallback(
        () => [
          { id: assetId, type: 'asset' },
          upload && { id: upload.id, type: 'file-upload' }
        ],
        [assetId, upload]
      )
    });

    const handleRemove = useCallback(() => {
      deleteResource({ id: assetId, type: 'asset' });
      if (upload) {
        deleteResource({ id: upload.id, type: 'file-upload' });
        if (upload.tusUpload) {
          Uploader.remove(upload.tusUpload);
        }
      }
    }, [upload, assetId, deleteResource]);

    const resumeUpload = useCallback(() => {
      upload.tusUpload && upload.tusUpload.start();
    }, [upload]);

    return (
      <Wrapped
        {...props}
        onRetryUpload={resumeUpload}
        onRemoveUpload={handleRemove}
      />
    );
  };
};
